import { useEffect, useState } from 'react';
import {
  Card, Title, Text,
  Table, TableHead, TableBody, TableRow, TableHeaderCell, TableCell,
} from '@tremor/react';
import { fetchInvAudit } from '../api/invEdit';
import type { InvAuditEntry, User } from '../api/types';
import { t } from '../lib/i18n';

/**
 * JCD-56 — "Historial de cambios" for one part+store.
 *
 * Reads the paceBI-side audit log, not the ERP audit table: the ERP row can
 * only carry the write daemon's service operator, so this is the only place
 * the real person's name appears. Failed attempts are listed too — the log
 * records every attempt, and a refused edit is exactly what someone
 * reviewing this surface wants to see.
 */
interface Props {
  sid: number;
  line: string;
  part: string;
  user: User;
  /** Bumped by the edit panel after a successful save so this refetches. */
  refreshKey: number;
}

function fieldLabel(wire: string): string {
  const m = t.part.edit.f as Record<string, string>;
  return m[wire] ?? wire;
}

export default function PartAuditPanel({ sid, line, part, user, refreshKey }: Props) {
  const canSee =
    user.role === 'owner' || (user.permissions ?? []).includes('inventory:edit');
  const [entries, setEntries] = useState<InvAuditEntry[] | null>(null);

  useEffect(() => {
    if (!canSee) return;
    let alive = true;
    fetchInvAudit(sid, line, part)
      .then((e) => {
        if (alive) setEntries(e);
      })
      .catch(() => {
        // A history panel that cannot load must not take the page down; it
        // simply shows as empty.
        if (alive) setEntries([]);
      });
    return () => {
      alive = false;
    };
  }, [sid, line, part, canSee, refreshKey]);

  if (!canSee || entries === null) return null;

  return (
    <Card className="mt-4">
      <Title>{t.part.audit.title}</Title>
      {entries.length === 0 ? (
        <Text className="mt-2">{t.part.audit.empty}</Text>
      ) : (
        <Table className="mt-2">
          <TableHead>
            <TableRow>
              <TableHeaderCell>{t.part.audit.cols.at}</TableHeaderCell>
              <TableHeaderCell>{t.part.audit.cols.user}</TableHeaderCell>
              <TableHeaderCell>{t.part.audit.cols.field}</TableHeaderCell>
              <TableHeaderCell>{t.part.audit.cols.old}</TableHeaderCell>
              <TableHeaderCell>{t.part.audit.cols.new}</TableHeaderCell>
              <TableHeaderCell>{t.part.audit.cols.result}</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.flatMap((e) =>
              (e.changes ?? []).map((c, i) => (
                <TableRow key={`${e.seq}-${i}`}>
                  <TableCell>{new Date(e.at).toLocaleString('es-MX')}</TableCell>
                  <TableCell>{e.user}</TableCell>
                  <TableCell>{fieldLabel(c.field)}</TableCell>
                  <TableCell>{c.old === null ? '—' : String(c.old)}</TableCell>
                  <TableCell>{c.new === null ? '—' : String(c.new)}</TableCell>
                  <TableCell>{e.result}</TableCell>
                </TableRow>
              )),
            )}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}
